"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Embedded Cognito auth context (ADR-014, session persistence per ADR-020)
 * — no Hosted-UI redirect window.
 *
 * The session (id/access/refresh tokens + expiry) is persisted in
 * `localStorage` under one key so a page reload or a second tab of the
 * same browser silently restores the signed-in state instead of forcing a
 * fresh login (owner-reported, 2026-08-24). This is the same storage
 * posture as Amplify Auth's default for Cognito SPAs; the XSS tradeoff it
 * reopens versus the earlier in-memory-only design is recorded in ADR-020
 * (accepted: seamless multi-tab UX for the pilot outweighs it, no
 * third-party scripts are shipped, and RevokeToken revokes this device's refresh token server-side).
 *
 * Cross-tab behavior: `storage` events keep every open tab in sync — sign
 * in or a token refresh in one tab updates the others; sign-out anywhere
 * signs out everywhere. Access/ID tokens are refreshed proactively before
 * expiry; an expired stored session is refreshed once on load and cleared
 * if the refresh token is no longer valid.
 */

import { amyTurnStore, clearPendingTurn } from "@/lib/amyTurnStore"
import { browserTurnStore } from "@/lib/browserTurnStore"
import { clearHealthConsentRequired } from "@/lib/healthConsentGate"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react"
import { loadCognitoConfig, type CognitoRuntimeConfig } from "@/lib/auth"
import { createCognitoClient, CognitoError, type CognitoClient } from "@/lib/cognito/client"
import { decodeIdTokenPayload } from "@/lib/cognito/jwt"
import { resetUser } from "@/lib/analytics"
import { cognitoErrorMessageKey } from "@/lib/cognito/errors"
import {
  buildAuthorizeUrl,
  createPkcePair,
  exchangeAuthorizationCode,
  randomState,
  type SocialProvider,
} from "@bitebalance/auth-core"

export type AuthStatus = "loading" | "signedOut" | "signedIn"

interface Session {
  idToken: string
  accessToken: string
  refreshToken: string
  /** Epoch ms when the access/id token expires. */
  expiresAt: number
}

export interface AuthContextValue {
  status: AuthStatus
  email: string | null
  /** i18n key for the last auth error, or null. Cleared on the next attempt. */
  errorKey: string | null
  clearError: () => void
  /**
   * Social providers this deployment has enabled (config-flagged, default
   * empty — see lib/auth.ts). Buttons render only for listed providers.
   */
  socialProviders: SocialProvider[]
  /**
   * Starts the authorization-code + PKCE redirect to the Cognito domain for
   * the given provider. Leaves the page on success (full redirect).
   */
  startSocialSignIn: (provider: SocialProvider) => Promise<void>
  /**
   * Completes the flow on /auth/callback: validates state, exchanges the
   * code, and enters the exact same `applySession` path as a password
   * sign-in (ADR-020 persistence, cross-tab sync, scheduled refresh).
   */
  completeSocialSignIn: (params: URLSearchParams) => Promise<void>
  signUp: (email: string, password: string) => Promise<{ userConfirmed: boolean }>
  confirmSignUp: (email: string, code: string) => Promise<void>
  resendCode: (email: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  forgotPassword: (email: string) => Promise<void>
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>
  /** Returns a valid access token, refreshing first if it's near expiry. Throws if signed out. */
  getAccessToken: () => Promise<string>
  getIdToken: () => Promise<string>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const REFRESH_SKEW_MS = 60_000
const SESSION_STORAGE_KEY = "bb_session_v1"

/**
 * In-flight OAuth transaction (state + PKCE verifier), written just before
 * the redirect to the Cognito domain and consumed exactly once on
 * /auth/callback. sessionStorage on purpose: scoped to this tab, gone when
 * the tab closes, never synced across tabs like the session itself is.
 */
const OAUTH_TX_STORAGE_KEY = "bb_oauth_tx_v1"

interface OAuthTransaction {
  state: string
  verifier: string
  redirectUri: string
}

function socialRedirectUri(): string {
  return `${window.location.origin}/auth/callback`
}

function readOAuthTransaction(): OAuthTransaction | null {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_TX_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<OAuthTransaction>
    if (
      typeof parsed.state !== "string" ||
      typeof parsed.verifier !== "string" ||
      typeof parsed.redirectUri !== "string"
    ) {
      return null
    }
    return parsed as OAuthTransaction
  } catch {
    return null
  }
}

function readStoredSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Session>
    if (
      typeof parsed.idToken !== "string" ||
      typeof parsed.accessToken !== "string" ||
      typeof parsed.refreshToken !== "string" ||
      typeof parsed.expiresAt !== "number"
    ) {
      return null
    }
    return parsed as Session
  } catch {
    return null
  }
}

function writeStoredSession(session: Session | null): void {
  try {
    if (session) {
      window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
    } else {
      window.localStorage.removeItem(SESSION_STORAGE_KEY)
    }
  } catch {
    // Storage may be unavailable (private mode quota etc.) — the in-memory
    // session still works for this tab; persistence just degrades.
  }
}

function emailFromIdToken(idToken: string): string | null {
  return (decodeIdTokenPayload(idToken)?.email as string | undefined) ?? null
}

export function AuthProvider({ children }: PropsWithChildren) {
  // Restore any persisted session synchronously on first render so a new
  // tab (or reload) with a still-valid token paints as signed-in without a
  // loading flash, and route guards never bounce a signed-in user.
  const [initialSession] = useState<Session | null>(() => readStoredSession())
  const [status, setStatus] = useState<AuthStatus>(
    initialSession && initialSession.expiresAt - Date.now() > REFRESH_SKEW_MS
      ? "signedIn"
      : "loading"
  )
  const [email, setEmail] = useState<string | null>(
    initialSession ? emailFromIdToken(initialSession.idToken) : null
  )
  // #106: clearSession runs from stable callbacks — a ref avoids a stale
  // email closure when clearing this user's Amy identity caches.
  const emailRef = useRef<string | null>(
    initialSession ? emailFromIdToken(initialSession.idToken) : null
  )
  useEffect(() => {
    emailRef.current = email
  }, [email])
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [socialProviders, setSocialProviders] = useState<SocialProvider[]>([])

  const clientRef = useRef<CognitoClient | null>(null)
  const configRef = useRef<CognitoRuntimeConfig | null>(null)
  const clientReadyRef = useRef<Promise<CognitoClient> | null>(null)
  const sessionRef = useRef<Session | null>(initialSession)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshInFlightRef = useRef<Promise<void> | null>(null)

  const getClient = useCallback(async (): Promise<CognitoClient> => {
    if (clientRef.current) return clientRef.current
    if (!clientReadyRef.current) {
      clientReadyRef.current = loadCognitoConfig().then(config => {
        const client = createCognitoClient(config)
        clientRef.current = client
        configRef.current = config
        return client
      })
    }
    return clientReadyRef.current
  }, [])

  const getConfig = useCallback(async (): Promise<CognitoRuntimeConfig> => {
    if (!configRef.current) await getClient()
    // getClient() always sets configRef before resolving.
    return configRef.current as CognitoRuntimeConfig
  }, [getClient])

  // Surface the deploy-time provider flags so the auth forms know which
  // social buttons to render (none, by default). Config load failures leave
  // the list empty — fail-closed to the email/password-only UI.
  useEffect(() => {
    let cancelled = false
    getConfig()
      .then(config => {
        if (!cancelled) setSocialProviders(config.socialProviders ?? [])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [getConfig])

  const clearSession = useCallback(() => {
    // The health/AI consent gate belongs to the account: the next person on this tab starts without it.
    clearHealthConsentRequired()
    // #106 shared-device hygiene: forget this user's Amy identity caches
    // (conversation id + runtime binding). The authoritative record is
    // server-side and per-user, so nothing is lost — the next sign-in of
    // the SAME user re-fetches the same durable conversation; a DIFFERENT
    // user never sees a stale id.
    const currentEmail = sessionRef.current ? emailRef.current : null
    if (currentEmail) {
      try {
        window.localStorage.removeItem(`bb_amy_conv_${currentEmail}`)
        window.sessionStorage.removeItem(`bb_amy_session_${currentEmail}`)
      } catch {
        /* best-effort */
      }
      // ADR-079: the pending-turn record (it quotes the user's message) and any poll die with the session.
      clearPendingTurn(browserTurnStore(), currentEmail)
    }
    amyTurnStore.dispatch({ type: "reset" })
    // Product analytics: forget whose session this was (shared-device hygiene, like the Amy caches above).
    resetUser()
    sessionRef.current = null
    writeStoredSession(null)
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    setEmail(null)
    setStatus("signedOut")
  }, [])

  const applySession = useCallback(
    (result: {
      idToken: string
      accessToken: string
      refreshToken: string
      expiresInSeconds: number
    }) => {
      const expiresAt = Date.now() + result.expiresInSeconds * 1000
      sessionRef.current = {
        idToken: result.idToken,
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt,
      }
      writeStoredSession(sessionRef.current)
      setEmail(emailFromIdToken(result.idToken))
      setStatus("signedIn")
      scheduleRefresh(expiresAt)
    },
    []
  )

  const runRefresh = useCallback(async (): Promise<void> => {
    const session = sessionRef.current
    if (!session) return
    if (refreshInFlightRef.current) return refreshInFlightRef.current

    const attempt = (async () => {
      try {
        const client = await getClient()
        const result = await client.refresh(session.refreshToken)
        applySession(result)
      } catch (err) {
        // 2026-08-30 (found via the mobile twin of this bug): only a
        // DEFINITIVE Cognito rejection may sign the user out — a network
        // blip must never destroy a valid session mid-flight.
        const code = err instanceof CognitoError ? err.code : null
        if (code === "NotAuthorizedException" || code === "UserNotFoundException") {
          clearSession()
        }
      } finally {
        refreshInFlightRef.current = null
      }
    })()

    refreshInFlightRef.current = attempt
    return attempt
  }, [applySession, clearSession, getClient])

  const scheduleRefresh = useCallback(
    (expiresAt: number) => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      const delay = Math.max(expiresAt - Date.now() - REFRESH_SKEW_MS, 0)
      refreshTimerRef.current = setTimeout(() => {
        void runRefresh()
      }, delay)
    },
    [runRefresh]
  )

  useEffect(() => {
    const stored = sessionRef.current
    if (stored) {
      if (stored.expiresAt - Date.now() > REFRESH_SKEW_MS) {
        // Restored a still-valid session (status is already "signedIn"
        // from the state initializer) — just arm the proactive refresh.
        scheduleRefresh(stored.expiresAt)
      } else {
        // Stored session is expired/near-expiry: one silent refresh.
        // Success re-enters signedIn; a DEFINITIVE rejection (revoked)
        // cleared the session inside runRefresh → signedOut. A transient
        // failure (network down at launch — owner incident 2026-08-30)
        // keeps the stored session and opens SIGNED IN offline-first:
        // on-demand refresh heals when the network returns, and a truly
        // revoked token gets its definitive rejection on that later
        // attempt and signs out then.
        void runRefresh().then(() => {
          setStatus(current =>
            current === "loading" ? (sessionRef.current ? "signedIn" : "signedOut") : current
          )
        })
      }
    } else {
      getClient()
        .then(() => setStatus(current => (current === "loading" ? "signedOut" : current)))
        .catch(() => setStatus("signedOut"))
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
    // Mount-only: restore/refresh must run exactly once per provider.
  }, [])

  // Cross-tab sync: `storage` fires in every OTHER tab of the origin when
  // the session key changes — sign-in/refresh propagates the new session,
  // sign-out (key removed) signs the other tabs out too.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== SESSION_STORAGE_KEY) return
      if (!event.newValue) {
        if (sessionRef.current) clearSession()
        return
      }
      try {
        const next = JSON.parse(event.newValue) as Session
        if (sessionRef.current?.accessToken === next.accessToken) return
        sessionRef.current = next
        setEmail(emailFromIdToken(next.idToken))
        setStatus("signedIn")
        scheduleRefresh(next.expiresAt)
      } catch {
        // Malformed value written by something else — ignore.
      }
    }
    window.addEventListener("storage", onStorage)
    return () => window.removeEventListener("storage", onStorage)
  }, [clearSession, scheduleRefresh])

  const withErrorHandling = useCallback(async <T,>(fn: () => Promise<T>): Promise<T> => {
    setErrorKey(null)
    try {
      return await fn()
    } catch (error) {
      setErrorKey(cognitoErrorMessageKey(error))
      throw error
    }
  }, [])

  const signUp = useCallback<AuthContextValue["signUp"]>(
    (signUpEmail, password) =>
      withErrorHandling(async () => {
        const client = await getClient()
        return client.signUp(signUpEmail, password)
      }),
    [getClient, withErrorHandling]
  )

  const confirmSignUp = useCallback<AuthContextValue["confirmSignUp"]>(
    (confirmEmail, code) =>
      withErrorHandling(async () => {
        const client = await getClient()
        await client.confirmSignUp(confirmEmail, code)
      }),
    [getClient, withErrorHandling]
  )

  const resendCode = useCallback<AuthContextValue["resendCode"]>(
    resendEmail =>
      withErrorHandling(async () => {
        const client = await getClient()
        await client.resendConfirmationCode(resendEmail)
      }),
    [getClient, withErrorHandling]
  )

  const signIn = useCallback<AuthContextValue["signIn"]>(
    (signInEmail, password) =>
      withErrorHandling(async () => {
        const client = await getClient()
        const result = await client.signIn(signInEmail, password)
        applySession(result)
      }),
    [applySession, getClient, withErrorHandling]
  )

  const signOut = useCallback<AuthContextValue["signOut"]>(async () => {
    const session = sessionRef.current
    if (session) {
      try {
        const client = await getClient()
        await client.signOut(session.refreshToken)
      } catch {
        // Local sign-out proceeds regardless of remote revoke success.
      }
    }
    clearSession()
  }, [clearSession, getClient])

  const forgotPassword = useCallback<AuthContextValue["forgotPassword"]>(
    forgotEmail =>
      withErrorHandling(async () => {
        const client = await getClient()
        await client.forgotPassword(forgotEmail)
      }),
    [getClient, withErrorHandling]
  )

  const confirmForgotPassword = useCallback<AuthContextValue["confirmForgotPassword"]>(
    (resetEmail, code, newPassword) =>
      withErrorHandling(async () => {
        const client = await getClient()
        await client.confirmForgotPassword(resetEmail, code, newPassword)
      }),
    [getClient, withErrorHandling]
  )

  const startSocialSignIn = useCallback<AuthContextValue["startSocialSignIn"]>(
    provider =>
      withErrorHandling(async () => {
        const config = await getConfig()
        if (!config.cognitoDomain || !config.socialProviders.includes(provider)) {
          // Button should never have rendered; fail with the generic key.
          throw new CognitoError("OAuthTokenError", `Provider ${provider} is not enabled`)
        }
        const { verifier, challenge } = createPkcePair()
        const state = randomState()
        const redirectUri = socialRedirectUri()
        const tx: OAuthTransaction = { state, verifier, redirectUri }
        window.sessionStorage.setItem(OAUTH_TX_STORAGE_KEY, JSON.stringify(tx))
        window.location.assign(
          buildAuthorizeUrl({
            domain: config.cognitoDomain,
            clientId: config.clientId,
            redirectUri,
            identityProvider: provider,
            state,
            codeChallenge: challenge,
          })
        )
      }),
    [getConfig, withErrorHandling]
  )

  const completeSocialSignIn = useCallback<AuthContextValue["completeSocialSignIn"]>(
    params =>
      withErrorHandling(async () => {
        const tx = readOAuthTransaction()
        // One-shot: a replayed/bookmarked callback URL must not retry the
        // exchange with a stale verifier.
        window.sessionStorage.removeItem(OAUTH_TX_STORAGE_KEY)

        const oauthError = params.get("error")
        if (oauthError) {
          // "access_denied" is the user backing out at the provider screen.
          throw new CognitoError(
            oauthError === "access_denied" ? "OAuthCancelled" : "OAuthTokenError",
            params.get("error_description") ?? oauthError
          )
        }

        const code = params.get("code")
        const returnedState = params.get("state")
        if (!tx || !code || !returnedState || returnedState !== tx.state) {
          throw new CognitoError("OAuthStateMismatch", "OAuth state validation failed")
        }

        const config = await getConfig()
        if (!config.cognitoDomain) {
          throw new CognitoError("OAuthTokenError", "No Cognito domain configured")
        }
        const result = await exchangeAuthorizationCode({
          domain: config.cognitoDomain,
          clientId: config.clientId,
          redirectUri: tx.redirectUri,
          code,
          codeVerifier: tx.verifier,
        })
        // Same entry point as the password flow: persists bb_session_v1,
        // schedules refresh, and fires the cross-tab storage sync (ADR-020).
        applySession(result)
      }),
    [applySession, getConfig, withErrorHandling]
  )

  const getAccessToken = useCallback<AuthContextValue["getAccessToken"]>(async () => {
    const session = sessionRef.current
    if (!session) {
      throw new Error("Not signed in")
    }
    if (session.expiresAt - Date.now() < REFRESH_SKEW_MS) {
      await runRefresh()
    }
    const current = sessionRef.current
    if (!current) {
      throw new Error("Not signed in")
    }
    return current.accessToken
  }, [runRefresh])

  // The REST API needs the ID token (email claim for /v1/me bootstrap; the
  // JWT authorizer accepts it). The access token stays for AgentCore
  // Runtime calls (T14).
  const getIdToken = useCallback<AuthContextValue["getIdToken"]>(async () => {
    const session = sessionRef.current
    if (!session) {
      throw new Error("Not signed in")
    }
    if (session.expiresAt - Date.now() < REFRESH_SKEW_MS) {
      await runRefresh()
    }
    const current = sessionRef.current
    if (!current) {
      throw new Error("Not signed in")
    }
    return current.idToken
  }, [runRefresh])

  const clearError = useCallback(() => setErrorKey(null), [])

  const value: AuthContextValue = {
    status,
    email,
    errorKey,
    clearError,
    signUp,
    confirmSignUp,
    resendCode,
    signIn,
    signOut,
    forgotPassword,
    confirmForgotPassword,
    socialProviders,
    startSocialSignIn,
    completeSocialSignIn,
    getAccessToken,
    getIdToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuthContext must be used within an AuthProvider")
  }
  return ctx
}
